import { api } from './api';
import type { Permission, PermissionRights, PermissionUser } from '@/types/permissions';

export interface ApiDocumentPermission {
  id: string;
  document_id: string;
  permission_type: 'user' | 'group' | 'tenant';
  permission_target: string;
  target_name?: string | null;
  can_read: boolean;
  can_write: boolean;
  can_delete: boolean;
  can_share: boolean;
  granted_at: string;
  granted_by: string;
  granted_by_name?: string | null;
  expires_at?: string | null;
}

interface ListPermissionsResponse {
  document_id: string;
  permissions: ApiDocumentPermission[];
}

interface ListTenantUsersResponse {
  users: Array<{
    id: string;
    name: string;
    email: string;
  }>;
}

const formatTargetName = (permission: ApiDocumentPermission): string => {
  if (permission.target_name) {
    return permission.target_name;
  }
  return permission.permission_target;
};

export const mapApiPermission = (permission: ApiDocumentPermission): Permission => ({
  id: permission.id,
  type: permission.permission_type,
  targetId: permission.permission_target,
  targetName: formatTargetName(permission),
  canRead: permission.can_read,
  canWrite: permission.can_write,
  canDelete: permission.can_delete,
  canShare: permission.can_share,
  grantedBy: permission.granted_by_name ?? permission.granted_by,
  grantedAt: permission.granted_at,
  expiresAt: permission.expires_at ?? undefined,
});

const toApiRights = (rights: PermissionRights) => ({
  can_read: rights.canRead,
  can_write: rights.canWrite,
  can_delete: rights.canDelete,
  can_share: rights.canShare,
});

class DocumentPermissionService {
  async listDocumentPermissions(documentId: string): Promise<Permission[]> {
    const response = await api.get<ListPermissionsResponse>(
      `/documents/${documentId}/permissions`,
    );
    return response.permissions.map(mapApiPermission);
  }

  async listTenantUsers(search?: string): Promise<PermissionUser[]> {
    const response = await api.get<ListTenantUsersResponse>('/tenant/users', {
      params: search ? { search } : undefined,
    });
    return response.users.map((user) => ({
      id: user.id,
      name: user.name,
      email: user.email,
    }));
  }

  async grantPermission(
    documentId: string,
    targetUserId: string,
    rights: PermissionRights,
  ): Promise<Permission> {
    const response = await api.post<ApiDocumentPermission>(
      `/documents/${documentId}/permissions`,
      {
        target_user_id: targetUserId,
        ...toApiRights(rights),
      },
    );
    return mapApiPermission(response);
  }

  async updatePermission(
    documentId: string,
    permissionId: string,
    rights: PermissionRights,
  ): Promise<Permission> {
    const response = await api.patch<ApiDocumentPermission>(
      `/documents/${documentId}/permissions/${permissionId}`,
      toApiRights(rights),
    );
    return mapApiPermission(response);
  }

  async revokePermission(documentId: string, permissionId: string): Promise<void> {
    await api.delete(`/documents/${documentId}/permissions/${permissionId}`);
  }
}

export const documentPermissionService = new DocumentPermissionService();
